import { Injectable } from '@angular/core';

export interface DroppedItem {
  /**
   * The file to upload, or `null` for a directory marker. Directory markers
   * are emitted for every folder walked so that empty folders still surface
   * via `collectFolderPaths` and get created at the destination.
   */
  file: File | null;
  /**
   * For files: folder names from the drop area to the file's parent folder.
   *   `[]` = file dropped directly at the root of the drop area.
   *   `['MyFolder']` = file is inside MyFolder.
   *   `['MyFolder', 'sub']` = file is inside MyFolder/sub.
   * For directory markers: the path of the directory itself (so an empty
   * top-level folder named `MyFolder` has relativePath `['MyFolder']`).
   */
  relativePath: string[];
}

/**
 * Walks an `HTML5 DataTransfer`'s items via `webkitGetAsEntry` so that
 * desktop-folder drops are flattened into a list of `File` + relative-path
 * entries. Falls back to `dataTransfer.files` for browsers / drops that
 * don't expose the entry API.
 */
@Injectable({ providedIn: 'root' })
export class VfFolderDropService {

  async readDropEntries(dt: DataTransfer | null): Promise<DroppedItem[]> {
    if (!dt) { return []; }
    const items = dt.items;
    if (items && items.length && this._hasEntryApi(items)) {
      const entries: any[] = [];
      for (let i = 0; i < items.length; i++) {
        const e = (items[i] as any).webkitGetAsEntry?.();
        if (e) { entries.push(e); }
      }
      // If any entry is a directory, walk all entries (mixed file+folder
      // drops route through the walker so files keep their root-level path).
      if (entries.some(e => e?.isDirectory)) {
        const result: DroppedItem[] = [];
        for (const e of entries) {
          await this._walk(e, [], result);
        }
        return result;
      }
    }

    // Fallback — flat file list (matches the pre-folder-drop behavior).
    const result: DroppedItem[] = [];
    if (dt.files) {
      for (let i = 0; i < dt.files.length; i++) {
        const f = dt.files[i];
        if (f) { result.push({ file: f, relativePath: [] }); }
      }
    }
    return result;
  }

  /** True if there's at least one folder in the dropped items. */
  hasFolders(items: DroppedItem[]): boolean {
    return items.some(i => i.relativePath.length > 0);
  }

  /**
   * Splits dropped items into the ones that should be uploaded, the ones that
   * are empty (size === 0) and the ones that exceed the size cap. Callers
   * surface the skipped buckets in their own toastr so a folder upload keeps
   * going past individual rejects.
   */
  filterValidUploadFiles(items: DroppedItem[], maxSize: number): {
    valid: DroppedItem[];
    skippedEmpty: string[];
    skippedOversized: string[];
  } {
    const valid: DroppedItem[] = [];
    const skippedEmpty: string[] = [];
    const skippedOversized: string[] = [];
    for (const item of items) {
      if (!item || !item.file) { continue; }
      if (item.file.size === 0) { skippedEmpty.push(item.file.name); continue; }
      if (item.file.size >= maxSize) { skippedOversized.push(item.file.name); continue; }
      valid.push(item);
    }
    return { valid, skippedEmpty, skippedOversized };
  }

  /**
   * Unique folder paths required to host the dropped files, sorted parents-first
   * so the caller can `createFolder` each in order with a guaranteed-resolved
   * parent.
   */
  collectFolderPaths(items: DroppedItem[]): string[][] {
    const seen = new Set<string>();
    const paths: string[][] = [];
    for (const item of items) {
      const acc: string[] = [];
      for (const seg of item.relativePath) {
        acc.push(seg);
        const key = acc.join('/');
        if (!seen.has(key)) {
          seen.add(key);
          paths.push(acc.slice());
        }
      }
    }
    paths.sort((a, b) => a.length - b.length);
    return paths;
  }

  private _hasEntryApi(items: DataTransferItemList): boolean {
    return typeof (items[0] as any)?.webkitGetAsEntry === 'function';
  }

  private async _walk(entry: any, parentPath: string[], out: DroppedItem[]): Promise<void> {
    if (!entry) { return; }
    if (entry.isFile) {
      const f = await this._getFile(entry);
      if (f) { out.push({ file: f, relativePath: parentPath }); }
      return;
    }
    if (entry.isDirectory) {
      const childPath = parentPath.concat(entry.name);
      const reader = entry.createReader();
      // readEntries returns up to ~100 entries per call; keep reading until
      // the directory is fully drained.
      while (true) {
        const batch: any[] = await new Promise<any[]>((res, rej) => {
          try { reader.readEntries((arr: any[]) => res(arr), rej); } catch (err) { rej(err); }
        }).catch(() => [] as any[]);
        if (!batch || batch.length === 0) { break; }
        for (const child of batch) {
          await this._walk(child, childPath, out);
        }
      }
    }
  }

  private _getFile(entry: any): Promise<File | null> {
    return new Promise<File | null>(res => {
      try { entry.file((f: File) => res(f), () => res(null)); } catch { res(null); }
    });
  }
}
